/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import { useState, useRef, useEffect } from 'react';
import type { ElementType } from 'react';
import {
  CircleStackIcon,
  GlobeAltIcon,
  LockClosedIcon,
  EllipsisVerticalIcon,
  FolderOpenIcon,
  KeyIcon,
  Cog6ToothIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import type { StorageBucket } from '../../types/objectStorage';
import { formatBytes } from '../../utils/formatBytes';
import type { BucketModalTab } from './BucketModal';
import { Tag } from '../console/ui';

interface Props {
  bucket: StorageBucket;
  onOpen: (tab: BucketModalTab) => void;
  onDelete: () => void;
  /** Shown under the region when the list spans several projects. */
  projectName?: string;
}

export default function BucketCard({ bucket, onOpen, onDelete, projectName }: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [menuOpen]);

  return (
    <div
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpen('files'); } }}
      className="ps-card relative hover:border-ps-strong transition-colors p-[18px] flex flex-col gap-3 cursor-pointer min-h-[150px] focus:outline-none focus-visible:ring-2 focus-visible:ring-ps-accent"
      onClick={() => onOpen('files')}
    >
      <div className="flex items-start gap-3">
        <span className="ps-icb w-9 h-9"><CircleStackIcon className="w-5 h-5" /></span>
        <div className="min-w-0 flex-1">
          <p className="m-0 text-[15px] font-medium text-ps-text truncate" title={bucket.name}>
            {bucket.name}
          </p>
          <p className="m-0 text-xs text-ps-subtle truncate">
            {bucket.region}
            {projectName ? ` · ${projectName}` : ''}
          </p>
        </div>

        <div ref={menuRef} className="relative" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="w-10 h-10 grid place-items-center rounded-md hover:bg-ps-hover text-ps-muted hover:text-ps-text"
            aria-label="Bucket actions"
          >
            <EllipsisVerticalIcon className="w-5 h-5" />
          </button>
          {menuOpen && (
            <div className="absolute right-0 mt-1 z-20 w-44 rounded-md border border-ps-border bg-ps-card shadow-lg py-1 text-sm">
              <MenuItem icon={FolderOpenIcon} label="Open files"   onClick={() => { setMenuOpen(false); onOpen('files'); }} />
              <MenuItem icon={KeyIcon}        label="Get keys"     onClick={() => { setMenuOpen(false); onOpen('access'); }} />
              <MenuItem icon={Cog6ToothIcon}  label="Settings"     onClick={() => { setMenuOpen(false); onOpen('settings'); }} />
              <div className="border-t border-ps-border my-1" />
              <MenuItem icon={TrashIcon}      label="Delete"       danger onClick={() => { setMenuOpen(false); onDelete(); }} />
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 text-[11px]">
        {bucket.is_public ? (
          <Tag tone="warn">
            <GlobeAltIcon className="w-3 h-3" /> Public
          </Tag>
        ) : (
          <Tag tone="off">
            <LockClosedIcon className="w-3 h-3" /> Private
          </Tag>
        )}
        {bucket.versioning && (
          <Tag tone="accent">Versioned</Tag>
        )}
      </div>

      <div className="mt-auto pt-3 border-t border-ps-raised flex items-end justify-between text-sm text-ps-text">
        <div>
          <p className="m-0 text-xs text-ps-subtle">Used</p>
          <p className="m-0">{formatBytes(bucket.current_size_bytes ?? 0)}</p>
        </div>
        <div className="text-right">
          <p className="m-0 text-xs text-ps-subtle">Objects</p>
          <p className="m-0">{(bucket.object_count ?? 0).toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
}

function MenuItem({
  icon: Icon, label, onClick, danger,
}: { icon: ElementType; label: string; onClick: () => void; danger?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`w-full min-h-[40px] px-3 flex items-center gap-2 text-left ${danger ? 'text-ps-err hover:bg-ps-err-bg' : 'text-ps-text hover:bg-ps-hover'}`}
    >
      <Icon className="w-4 h-4" />
      {label}
    </button>
  );
}
